/* eslint-disable @typescript-eslint/no-explicit-any, max-classes-per-file */

import clientEnv from '../../utils/global/clientEnv'
import { storeRedirectUrl } from '../../routes/logged-in/utils/loggedIn.utils'
import { errors } from '../../utils/error-handling/errors'
import deprecatedAnalytics from './deprecatedAnalytics'
import request, { type RequestOpts, type HttpMethod } from './request'
import { ROUTE_ROOT } from './constants'
import ApiResponseError from './errors/ApiResponseError'
import { type User } from './types'
import HttpError from './errors/HttpError'
import { trackLogout } from '../../gin-and-tonic/containers/log-out/utils/trackLogout'
import { TokenRefreshService } from '../../api/utils/auth/tokenRefresh'
import {
  getSessionFromStorage,
  setSessionInStorage,
  removeSessionData,
} from '../../api/utils/apollo/session/sessionToken.utils'

const RATE_LIMIT_RETRY_MAX_ATTEMPTS = 3
const RATE_LIMIT_RETRY_BASE_DELAY_MS = 1000

function getRateLimitDelay(e: HttpError, attempt: number): number {
  const retryAfterHeader = e.headers.get('Retry-After')
  if (retryAfterHeader) {
    const retryAfterSeconds = parseFloat(retryAfterHeader)
    if (!isNaN(retryAfterSeconds) && retryAfterSeconds > 0) {
      return retryAfterSeconds * 1000
    }
  }
  return RATE_LIMIT_RETRY_BASE_DELAY_MS * Math.pow(2, attempt)
}

export type ApiRequestOpts<TData = {}, TQuery = {}> = Omit<RequestOpts<TData, TQuery>, 'method'>

export type GoFn<TMethod extends HttpMethod> = TMethod extends 'get'
  ? <TResponse, TQuery = {}>(
      path: string,
      apiRequestOpts?: ApiRequestOpts<undefined, TQuery>
    ) => Promise<TResponse>
  : <TResponse, TData = {}, TQuery = {}>(
      path: string,
      apiRequestOpts?: ApiRequestOpts<TData, TQuery>
    ) => Promise<TResponse>

export type Go = {
  get: GoFn<'get'>
  post: GoFn<'post'>
  put: GoFn<'put'>
  delete: GoFn<'delete'>
}

interface LoginResponse {
  token: string
}

function createGo({ env, onUnauthorized }: { env: Env; onUnauthorized: () => void }): Go {
  const go: Go = {
    get: createGoFn('get'),
    post: createGoFn('post'),
    put: createGoFn('put'),
    delete: createGoFn('delete'),
  }

  function createGoFn<TMethod extends HttpMethod>(method: TMethod) {
    async function goRequest<TResponse, TOpts extends ApiRequestOpts<any, any>>(
      path: string,
      apiRequestOpts?: TOpts,
      rateLimitAttempt = 0
    ): Promise<TResponse> {
      try {
        const { url, opts } = await getRequestParams<TOpts>(method, path, apiRequestOpts)
        return await request<TResponse>(url, opts)
      } catch (e: any) {
        // fetch will throw an error if there is a network issue (cannot reach server)
        // otherwise, fetch checks the response status and throw an HttpError for non 2xx 3xx responses
        if (!(e instanceof HttpError)) {
          // Silently re-throw aborted requests — they were intentionally cancelled
          if (e instanceof DOMException && e.name === 'AbortError') {
            throw e
          }

          // fetch threw
          // error has no http status associated with it, likely a CORS or network issue
          errors.captureApiError(e, {
            extra: {
              message: `Network error with error + ${e}`,
            },
          })
          throw e
        }

        // Only attempt token refresh/logout for non-auth endpoints
        // This prevents infinite loops (e.g., /auth/login returning 401 shouldn't redirect to login again)
        // and ensures legitimate auth errors surface to the user (e.g., wrong password shows error message)
        if (e.status === 401 && !path.match(/auth\/(\w+)/)) {
          // Check if refresh token exists before attempting refresh
          const canRefresh = await TokenRefreshService.canRefreshAccessToken()

          if (!canRefresh) {
            // Expected for users who didn't opt into "Keep me logged in"
            // No refresh token available, just redirect to login
            console.info('No refresh token available, redirecting to login')
            await onUnauthorized()
            throw e
          }

          try {
            // Attempt token refresh before logging out
            // NOTE: This legacy API client is being phased out in favor of GraphQL
            // but still used in some old sections, so we add refresh support for consistency
            const newToken = await TokenRefreshService.refreshAccessToken()

            // Update stored session
            await env.set('session', { token: newToken })

            // Retry the original request with new token
            const retryParams = await getRequestParams(method, path, apiRequestOpts)
            return request<TResponse>(retryParams.url, retryParams.opts)
          } catch (refreshError) {
            // This is a real error - refresh token exists but refresh failed
            console.error('Token refresh failed on 401:', refreshError)
            errors.captureApiError(e, {
              extra: {
                message: `Unauthorized error after refresh failed: ${e}`,
              },
            })
            await onUnauthorized()
            throw e
          }
        }

        if (e.status === 429 && rateLimitAttempt < RATE_LIMIT_RETRY_MAX_ATTEMPTS) {
          const delay = getRateLimitDelay(e, rateLimitAttempt)
          await new Promise(resolve => setTimeout(resolve, delay))
          return goRequest<TResponse, TOpts>(path, apiRequestOpts, rateLimitAttempt + 1)
        }

        const error = await getApiResponseError(e)

        errors.captureApiError(error, {
          extra: {
            message: `API error with error + ${error}`,
            url: e.url,
            requestId: e.headers.get('x-request-id'),
          },
        })

        throw error
      }
    }

    return goRequest
  }

  async function getRequestParams<TOpts extends ApiRequestOpts<any, any>>(
    method: HttpMethod,
    path: string,
    apiRequestOpts?: TOpts
  ): Promise<{ url: string; opts: RequestOpts<any, any> }> {
    const url = env.apiRoot + path
    const session = await env.get<{ token: string }>('session')
    let headers

    if (session?.token) {
      headers = {
        Authorization: `Bearer ${session.token}`,
      }
    }

    const opts: RequestOpts<any, any> = {
      method,
      headers,
      credentials: 'include',
      ...apiRequestOpts,
    }

    return { url, opts }
  }

  return go
}

async function getApiResponseError(e: HttpError) {
  let message = `API Error: ${e.status}`
  let apiErrors

  if (e.body && Object.prototype.hasOwnProperty.call(e.body, 'errors')) {
    const { errors } = e.body

    if (Array.isArray(errors) && errors.length) {
      message = errors.toString()
      apiErrors = errors
    } else if (typeof errors === 'string') {
      message = errors
      apiErrors = [errors]
    } else if (typeof errors === 'object' && typeof (errors as Record<string, unknown>)['error'] === 'string') {
      message = (errors as Record<string, unknown>)['error'] as string
      apiErrors = [(errors as Record<string, unknown>)['error'] as string]
    }
  }

  return new ApiResponseError(message, e.status, apiErrors)
}

class Env {
  mode: NodeEnv

  constructor(opts: { mode: NodeEnv }) {
    this.mode = opts.mode
  }

  get apiRoot(): string {
    return `${clientEnv.PUBLIC_CLIENT_RPP_URL}/api`
  }

  // Session storage uses shared utilities to align with GraphQL client
  get<T>(key: string): T | null {
    if (key === 'session') {
      return getSessionFromStorage() as T | null
    }
    return null
  }

  set(key: string, data: { token: string } | undefined): void {
    if (key === 'session') {
      if (data?.token) {
        setSessionInStorage(data)
      } else {
        removeSessionData()
      }
    }
  }

  del(key: string): void {
    if (key === 'session') {
      removeSessionData()
    }
  }
}

class Instacart {
  env!: Env

  go!: Go

  auth: User | null = null

  init(opts: { mode: NodeEnv } = { mode: 'development' }) {
    this.env = new Env(opts)
    this.go = createGo({
      env: this.env,
      onUnauthorized: () => this.logoutAndRedirect(),
    })
    this.auth = null
  }

  async getAuthInfo(): Promise<User | null> {
    if (this.auth) return this.auth
    const token = await this.env.get('session')
    if (!token) return null

    try {
      const { data: auth } = await this.go.get<{ data: User }>('/v1/auth/user')
      this.auth = auth
    } catch (e) {
      // noop
    }

    return this.auth
  }

  async isLoggedIn() {
    const session = await this.env.get('session')

    return !!session
  }

  async login(email: string, password: string) {
    const { data } = await this.go.post<{ data: LoginResponse }>('/v1/auth/login', {
      data: { email, password },
    })
    await this.env.set('session', data)
    const user = await this.getAuthInfo()
    deprecatedAnalytics.track('login.login_complete', {
      user_id: user && user.uuid,
    })

    return data
  }

  async logout(isExpired: boolean = false) {
    trackLogout({
      isExpired,
    })

    await this.go.post('/v1/auth/logout')
    await this.env.del('session')
    this.auth = null

    return this
  }

  async logoutAndRedirect() {
    const { pathname, search, hash } = window.location

    storeRedirectUrl({ pathname, search, hash })

    try {
      await this.logout(true)
      window.location.href = ROUTE_ROOT
    } catch (e) {
      // noop
    }
  }
}

export default (() => new Instacart())()
